/**
 * Table.tsx
 * ----------------
 * Implementation of a table that can optionally be sorted or not.
 */

import * as React from 'react';

import { classNames } from '../../utilities/classNames';
import { isString } from '../../utilities/typeGuards';
import { SortDownIcon, SortUpIcon } from './FluentSvgIcon';

/**
 * Component properties
 */

interface TableProps<T> {
    columns: TableColumnProps<T>[];
    data: T[];
    name: string;
    reflowOptions?: {
        renderSideSection?: (datum: T) => JSX.Element;
        renderTitle: (datum: T) => JSX.Element;
    };
    sortable?: boolean;
}

interface TableBodyCellProps<T> {
    cellClassName?: string | ((datum: T) => string);
    datum: T;
    nonInlined?: boolean;
    render: (datum: T) => JSX.Element;
}

interface ReflowingTableBodyCellProps<T> extends TableBodyCellProps<T> {
    headerClassName?: string;
    isColumnSortable?: boolean;
    isTableSortable: boolean;
    onHeaderCellClick: () => void;
    sortDirection?: 'asc' | 'desc';
    title: string;
}

interface TableColumnProps<TData> {
    cellClassName?: string | ((datum: TData) => string);
    compareTo?: (a: TData, b: TData) => number;
    group?: (datum: TData) => string | number;
    groupCompareTo?: (a: unknown, b: unknown) => number;
    groupTitle?: (group: unknown) => string;
    headerClassName?: string;
    hideOnReflow?: boolean;
    render: (datum: TData) => JSX.Element;
    sortable?: boolean;
    sortMode?: 'grouped' | 'ungrouped';
    title: string;
}

interface TableGroupProps<T> {
    columns: TableColumnProps<T>[];
    data: T[];
    isLastGroup?: boolean;
    onHeaderCellClick: (index: number) => void;
    reflowOptions?: {
        renderSideSection?: (datum: T) => JSX.Element;
        renderTitle: (datum: T) => JSX.Element;
    };
    sortable?: boolean;
    sortColumn?: number;
    sortDirection?: 'asc' | 'desc';
    title: string;
}

interface TableHeaderCellProps {
    headerClassName?: string;
    isColumnSortable?: boolean;
    isTableSortable: boolean;
    nonInlined?: boolean;
    onClick: () => void;
    sortDirection?: 'asc' | 'desc';
    title: string;
}

interface TableHeaderColumnProps {
    headerClassName?: string;
    hideOnReflow?: boolean;
    sortable?: boolean;
    title: string;
}

interface TableHeaderProps {
    className?: string;
    columns: TableHeaderColumnProps[];
    isTableSortable: boolean;
    onHeaderCellClick: (index: number) => void;
    sortColumn?: number;
    sortDirection?: 'asc' | 'desc';
}

interface TableRowProps<T> {
    columns: TableColumnProps<T>[];
    datum: T;
    isLastRow?: boolean;
}

interface ReflowingTableRowProps<T> extends TableRowProps<T> {
    isTableSortable: boolean;
    onHeaderCellClick: (index: number) => void;
    reflowOptions?: {
        renderSideSection?: (datum: T) => JSX.Element;
        renderTitle: (datum: T) => JSX.Element;
    };
    sortColumn?: number;
    sortDirection?: 'asc' | 'desc';
}

/**
 * Private functions
 */

function isSortable (props: TableHeaderCellProps) {
    const { isColumnSortable, isTableSortable } = props;

    if (!isTableSortable) {
        return false;
    }

    // Note: checking strict equivalence to false because undefined means default, which in this case is true
    if (isColumnSortable === false) {
        return false;
    }

    return true;
}

/**
 * Implementation of a table that can optionally be sorted or not.
 * @param props Component properties
 * @returns Rendered content
 */
export function Table<T> (props: TableProps<T>) {
    const { columns, data, name, reflowOptions, sortable } = props;

    // Component state hooks
    const [sortColumn, setSortColumn] = React.useState<number | undefined>(undefined);
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | undefined>(undefined);

    // Callback hooks
    const onColumnHeaderClicked = React.useCallback((columnIndex: number) => {
        // Sorting column was changed
        if (columnIndex !== sortColumn) {
            setSortColumn(columnIndex);
            setSortDirection('asc');
            return;
        }

        // Same column, no sort -> asc
        if (sortDirection === undefined) {
            setSortDirection('asc');
            return;
        }

        // Same column, asc -> desc
        if (sortDirection === 'asc') {
            setSortDirection('desc');
            return;
        }

        // desc -> no sort
        setSortColumn(undefined);
        setSortDirection(undefined);
    }, [setSortColumn, setSortDirection, sortColumn, sortDirection]);

    // Validation
    columns.forEach(column => {
        const { group, groupCompareTo, groupTitle, sortable: isColumnSortable, sortMode, title } = column;

        if (sortable && isColumnSortable && sortMode === 'grouped' && group === undefined) {
            throw new Error(`Sortable column '${title}' is in 'grouped' sort mode, but no grouping function was given.`);
        }

        if (sortable && isColumnSortable && sortMode === 'grouped' && groupCompareTo === undefined) {
            throw new Error(`Sortable column '${title}' is in 'grouped' sort mode, but no compareTo function for groups was given.`);
        }

        if (sortable && isColumnSortable && sortMode === 'grouped' && groupTitle === undefined) {
            throw new Error(`Sortable column '${title}' is in 'grouped' sort mode, but no title function for groups was given.`);
        }
    });

    if (sortColumn !== undefined && (sortColumn < 0 || sortColumn >= columns.length)) {
        throw new Error(`Column #${sortColumn} was selected for sorting, but that index is out-of-bounds of the given columns list.`);
    }

    // If sorting is applied in grouped mode, render groups
    if (sortColumn !== undefined && sortDirection !== undefined && columns[sortColumn].sortMode === 'grouped') {
        const { group, groupCompareTo, groupTitle } = columns[sortColumn];

        if (group === undefined || groupCompareTo === undefined || groupTitle === undefined) {
            throw new Error('Grouped sorting was selected, but no group, compareTo, or title function was given.');
        }

        const groupedData: {
            [key: string]: Array<T>;
        } = {};
        data.forEach((item: T) => {
            const key = group(item);
            if (groupedData[key] === undefined) {
                groupedData[key] = new Array<T>();
            }

            groupedData[key].push(item);
        });

        const sortedKeys = Object.keys(groupedData).sort((a, b) => {
            const compareToFn = groupCompareTo as (a: unknown, b: unknown) => number;

            return compareToFn(a, b) * (sortDirection === 'desc' ? -1 : 1);
        });

        return (
            <div aria-label={name} className="table" role="table">
                {(sortedKeys || []).map((groupKey, i) => {
                    const dataSet = groupedData[groupKey] as T[];

                    return (
                        <TableGroup
                            columns={columns}
                            data={dataSet}
                            isLastGroup={i === sortedKeys.length - 1}
                            key={i}
                            onHeaderCellClick={onColumnHeaderClicked}
                            reflowOptions={reflowOptions}
                            sortable={sortable}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            title={(groupTitle as (group: unknown) => string)(groupKey)} />
                    );
                })}
            </div>
        );
    }

    // If sorting is applied but not in grouped mode, sort the data accordingly
    if (sortColumn !== undefined && sortDirection !== undefined && columns[sortColumn].sortMode === 'ungrouped') {
        const { compareTo } = columns[sortColumn];

        // Note: spread-copy syntax used here to avoid in-place sort on passed array
        const sortedData = [...data].sort((a, b) => {
            const compareToFn = compareTo as (a: T, b: T) => number;

            return compareToFn(a, b) * (sortDirection === 'desc' ? -1 : 1);
        });

        return (
            <div aria-label={name} className="table" role="table">
                <TableHeader
                    className="hidden--width-800-down"
                    columns={columns}
                    isTableSortable={sortable === undefined || sortable}
                    onHeaderCellClick={onColumnHeaderClicked}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection} />

                <div className="table__body">
                    {sortedData.map((datum, i) => {
                        return <TableRow
                            columns={columns}
                            datum={datum}
                            isLastRow={i === sortedData.length - 1}
                            key={i} />;
                    })}

                    {sortedData.map((datum, i) => {
                        return <ReflowingTableRow
                            columns={columns}
                            datum={datum}
                            isLastRow={i === sortedData.length - 1}
                            isTableSortable={sortable === undefined || sortable}
                            key={i}
                            onHeaderCellClick={onColumnHeaderClicked}
                            reflowOptions={reflowOptions}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection} />;
                    })}
                </div>
            </div>
        );
    }

    return (
        <div aria-label={name} className="table" role="table">
            <TableHeader
                className="hidden--width-800-down"
                columns={columns}
                isTableSortable={sortable === undefined || sortable}
                onHeaderCellClick={onColumnHeaderClicked}
                sortColumn={sortColumn}
                sortDirection={sortDirection} />

            <div className="table__body">
                {data.map((datum, i) => {
                    return <TableRow
                        columns={columns}
                        datum={datum}
                        isLastRow={i === data.length - 1}
                        key={i} />;
                })}

                {data.map((datum, i) => {
                    return <ReflowingTableRow
                        columns={columns}
                        datum={datum}
                        isLastRow={i === data.length - 1}
                        isTableSortable={sortable === undefined || sortable}
                        key={i}
                        onHeaderCellClick={onColumnHeaderClicked}
                        reflowOptions={reflowOptions}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection} />;
                })}
            </div>
        </div>
    );
}

function TableBodyCell<T> (props: TableBodyCellProps<T>) {
    const { cellClassName, datum, render } = props;

    const classes = classNames(props)
        .add('table__body-cell')
        .add('table__body-cell--non-inlined', props => props.nonInlined !== undefined && props.nonInlined);

    if (isString(cellClassName)) {
        classes.add(cellClassName);
    } else if (cellClassName !== undefined) {
        classes.add(cellClassName(datum));
    }

    return (
        <div className={classes.value()} role="cell">
            {render(datum)}
        </div>
    );
}

function ReflowingTableBodyCell<T> (props: ReflowingTableBodyCellProps<T>) {
    const {
        cellClassName, datum, headerClassName, isColumnSortable, isTableSortable, onHeaderCellClick, render,
        sortDirection, title,
    } = props;

    const classes = classNames(props).add('table__body-cell-container');

    return (
        <div className={classes.value()}>
            <TableHeaderCell
                headerClassName={headerClassName}
                isColumnSortable={isColumnSortable}
                isTableSortable={isTableSortable}
                nonInlined
                onClick={onHeaderCellClick}
                sortDirection={sortDirection}
                title={title} />

            <TableBodyCell
                cellClassName={cellClassName}
                datum={datum}
                nonInlined
                render={render} />
        </div>
    );
}

function TableGroup<T> (props: TableGroupProps<T>) {
    const {
        columns, data, isLastGroup, onHeaderCellClick, reflowOptions, sortable, sortColumn, sortDirection, title,
    } = props;

    const classes = classNames(props)
        .add('table__group')
        .add('table__group--last', props => props.isLastGroup === true);

    // If sorting is applied, sort!
    if (sortColumn !== undefined && sortDirection !== undefined) {
        const { compareTo } = columns[sortColumn];

        // Note: spread-copy syntax used here to avoid in-place sort on passed array
        const sortedData = [...data].sort((a, b) => {
            const compareToFn = compareTo as (a: T, b: T) => number;

            return compareToFn(a, b) * (sortDirection === 'desc' ? -1 : 1);
        });

        return (
            <div className={classes.value()}>
                <div className="table__group-title">
                    {title}
                </div>

                <TableHeader
                    className="hidden--width-800-down"
                    columns={columns}
                    isTableSortable={sortable === undefined || sortable}
                    onHeaderCellClick={onHeaderCellClick}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection} />

                <div className="table__body" role="rowgroup">
                    {sortedData.map((datum, i) => {
                        return <TableRow
                            columns={columns}
                            datum={datum}
                            isLastRow={isLastGroup === true && i === sortedData.length - 1}
                            key={i} />;
                    })}

                    {sortedData.map((datum, i) => {
                        return <ReflowingTableRow
                            columns={columns}
                            datum={datum}
                            isLastRow={isLastGroup === true && i === sortedData.length - 1}
                            isTableSortable={sortable === undefined || sortable}
                            key={i}
                            onHeaderCellClick={onHeaderCellClick}
                            reflowOptions={reflowOptions}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection} />;
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className={classes.value()}>
            <div className="table__group-title">
                {title}
            </div>

            <TableHeader
                className="hidden--width-800-down"
                columns={columns}
                isTableSortable={sortable === undefined || sortable}
                onHeaderCellClick={onHeaderCellClick}
                sortColumn={sortColumn}
                sortDirection={sortDirection} />

            <div className="table__body" role="rowgroup">
                {data.map((datum, i) => {
                    return <TableRow
                        columns={columns}
                        datum={datum}
                        isLastRow={isLastGroup === true && i === data.length - 1}
                        key={i} />;
                })}

                {data.map((datum, i) => {
                    return <ReflowingTableRow
                        columns={columns}
                        datum={datum}
                        isLastRow={isLastGroup === true && i === data.length - 1}
                        isTableSortable={sortable === undefined || sortable}
                        key={i}
                        onHeaderCellClick={onHeaderCellClick}
                        reflowOptions={reflowOptions}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection} />;
                })}
            </div>
        </div>
    );
}

function TableHeader (props: TableHeaderProps) {
    const { className, columns, isTableSortable, onHeaderCellClick, sortColumn, sortDirection } = props;

    const classes = className !== undefined
        ? classNames(props).add('table__header').add(className)
        : classNames(props).add('table__header');

    return (
        <div className={classes.value()} role="row">
            {columns.map((column, i) => {
                const { headerClassName, sortable: isColumnSortable, title } = column;

                return <TableHeaderCell
                    headerClassName={headerClassName}
                    isColumnSortable={isColumnSortable}
                    isTableSortable={isTableSortable}
                    key={i}
                    onClick={() => onHeaderCellClick(i)}
                    sortDirection={sortColumn === i ? sortDirection : undefined}
                    title={title} />;
            })}
        </div>
    );
}

function TableHeaderCell (props: TableHeaderCellProps) {
    const { headerClassName, onClick, sortDirection, title } = props;
    const allowSorting = isSortable(props);

    const classes = classNames(props)
        .add('table__header-cell')
        .add('table__header-cell--sortable', isSortable)
        .add('table__header-cell--non-inlined', props => props.nonInlined !== undefined && props.nonInlined)
        .add(headerClassName as string, props => props.headerClassName !== undefined);

    // When the column is sortable, allow for keyboard navigation by making the cell a button
    if (allowSorting) {
        return (
            <button className={classes.value()} onClick={() => onClick()} role="columnheader">
                {title} {sortDirection === 'asc' && <SortUpIcon className="table__sort-direction" />}{sortDirection === 'desc' && <SortDownIcon className="table__sort-direction" />}
            </button>
        );
    }

    return (
        <div className={classes.value()} role="columnheader">
            {title} {sortDirection === 'asc' && <SortUpIcon className="table__sort-direction" />}{sortDirection === 'desc' && <SortDownIcon className="table__sort-direction" />}
        </div>
    );
}

function TableRow<T> (props: TableRowProps<T>) {
    const { columns, datum } = props;

    const classes = classNames(props)
        .add('table__row')
        .add('table__row--last', props => props.isLastRow === true)
        .add('hidden--width-800-down');

    return (
        <div className={classes.value()} role="row">
            {columns.map((column, i) => {
                const { cellClassName, render } = column;

                return (
                    <TableBodyCell
                        cellClassName={cellClassName}
                        datum={datum}
                        key={i}
                        render={render} />
                );
            })}
        </div>
    );
}

function ReflowingTableRow<T> (props: ReflowingTableRowProps<T>) {
    const { columns, datum, isTableSortable, onHeaderCellClick, reflowOptions, sortColumn, sortDirection } = props;

    const classes = classNames(props)
        .add('table__row')
        .add('table__row--last', props => props.isLastRow === true)
        .add('table__row--reflowing')
        .add('hidden--width-800-up');

    return (
        <div className={classes.value()} role="row">
            {reflowOptions !== undefined && reflowOptions.renderSideSection !== undefined &&
                <div className="table__row-reflow-side-section">
                    {reflowOptions.renderSideSection(datum)}
                </div>}

            <div className="table__row-reflow-content">
                {reflowOptions !== undefined &&
                    <div className="table__row-reflow-title">
                        {reflowOptions.renderTitle(datum)}
                    </div>}

                <div className="table__row-reflow-columns">
                    {columns
                        .filter(column => column.hideOnReflow === undefined || !column.hideOnReflow)
                        .map(column => {
                            const {
                                cellClassName, headerClassName, render, sortable: isColumnSortable, title,
                            } = column;
                            const columnIndex = columns.indexOf(column);

                            return <ReflowingTableBodyCell
                                cellClassName={cellClassName}
                                datum={datum}
                                headerClassName={headerClassName}
                                isColumnSortable={isColumnSortable}
                                isTableSortable={isTableSortable}
                                onHeaderCellClick={() => onHeaderCellClick(columnIndex)}
                                render={render}
                                sortDirection={sortColumn === columnIndex ? sortDirection : undefined}
                                title={title} 
                                key={columnIndex} />;
                        })}
                </div>
            </div>
        </div>
    );
}
